import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import TopNavigation from "components/ui/TopNavigation";
import QuickActionToolbar from "components/ui/QuickActionToolbar";
import NotificationAlertBar from "components/ui/NotificationAlertBar";
import Icon from "components/AppIcon";

import KPICard from "./components/KPICard";
import SalesTrendChart from "./components/SalesTrendChart";
import TerritoryBarChart from "./components/TerritoryBarChart";
import ProductMixChart from "./components/ProductMixChart";
import QuickNavPanel from "./components/QuickNavPanel";
import TopAccountsTable from "./components/TopAccountsTable";
import RecentActivityFeed from "./components/RecentActivityFeed";

const kpiData = [
  {
    id: "total-sales",
    title: "Total Sales (MTD)",
    value: "₱18.06M",
    subtitle: "March 2026",
    trend: "up",
    trendValue: "+9.2%",
    icon: "DollarSign",
    color: "#DC2626",
    path: "/performance-analytics",
  },
  {
    id: "volume-sold",
    title: "Volume Sold",
    value: "284,720",
    subtitle: "Units this month",
    trend: "up",
    trendValue: "+6.4%",
    icon: "Package",
    color: "#D4AF37",
    path: "/sales-ordering-system",
  },
  {
    id: "orders-per-day",
    title: "Orders / Day",
    value: "187",
    subtitle: "Avg last 7 days",
    trend: "up",
    trendValue: "+3.1%",
    icon: "ShoppingCart",
    color: "#22C55E",
    path: "/sales-ordering-system",
  },
  {
    id: "avg-order-value",
    title: "Avg Order Value",
    value: "₱3,240",
    subtitle: "Per transaction",
    trend: "up",
    trendValue: "+1.8%",
    icon: "Receipt",
    color: "#D4AF37",
    path: "/performance-analytics",
  },
  {
    id: "active-accounts",
    title: "Active Accounts",
    value: "1,024",
    subtitle: "Out of 1,248 total",
    trend: "neutral",
    trendValue: "82.1%",
    icon: "Users",
    color: "#8B92A5",
    path: "/account-management",
  },
  {
    id: "coverage",
    title: "Coverage %",
    value: "78.4%",
    subtitle: "Accounts visited MTD",
    trend: "up",
    trendValue: "+4.2%",
    icon: "MapPin",
    color: "#DC2626",
    path: "/philippine-map-visualization",
  },
  {
    id: "revenue",
    title: "Gross Revenue",
    value: "₱21.3M",
    subtitle: "Before deductions",
    trend: "up",
    trendValue: "+11.5%",
    icon: "TrendingUp",
    color: "#D4AF37",
    path: "/performance-analytics",
  },
];

export default function DashboardOverview() {
  const navigate = useNavigate();
  const [lastRefreshed] = useState(new Date());

  const handleKPIClick = (path) => navigate(path);

  const handleToolbarAction = (action) => {
    if (action === "refresh") window.location?.reload();
    if (action === "export") alert("Exporting dashboard report...");
  };

  return (
    <div className="min-h-screen bg-background page-gradient-bg">
      <TopNavigation />
      <NotificationAlertBar />
      <QuickActionToolbar onAction={handleToolbarAction} />
      <main className="px-4 md:px-6 lg:px-8 py-3 md:py-4 lg:py-6 pb-20 lg:pb-8">
        {/* Page Header */}
        <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
          <div>
            <h1 className="font-heading text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-foreground">
              Dashboard Overview
            </h1>
            <p className="font-caption text-xs sm:text-sm text-muted-foreground mt-1">
              All territories &nbsp;·&nbsp; March 2026 &nbsp;·&nbsp; Updated{" "}
              {lastRefreshed?.toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit" })}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full" style={{ background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="font-caption text-xs font-medium" style={{ color: '#22C55E' }}>Live Data</span>
            </div>
            <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full" style={{ background: 'rgba(220, 38, 38, 0.1)', border: '1px solid rgba(220, 38, 38, 0.3)' }}>
              <Icon name="Shield" size={12} color="#DC2626" strokeWidth={2} />
              <span className="font-caption text-xs font-medium" style={{ color: '#DC2626' }}>National Manager</span>
            </div>
          </div>
        </div>

        {/* KPI Grid */}
        <section aria-label="Key Performance Indicators" className="section-gap">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-2 sm:gap-3 md:gap-4">
            {kpiData?.map((kpi) => (
              <KPICard
                key={kpi?.id}
                title={kpi?.title}
                value={kpi?.value}
                subtitle={kpi?.subtitle}
                trend={kpi?.trend}
                trendValue={kpi?.trendValue}
                icon={kpi?.icon}
                color={kpi?.color}
                onClick={() => handleKPIClick(kpi?.path)}
              />
            ))}
          </div>
        </section>

        {/* Charts Row */}
        <section aria-label="Performance Charts" className="section-gap">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 lg:gap-5">
            <div className="md:col-span-1 lg:col-span-2">
              <SalesTrendChart />
            </div>
            <div>
              <ProductMixChart />
            </div>
          </div>
        </section>

        {/* Territory Bar Chart */}
        <section aria-label="Territory Comparison" className="section-gap">
          <TerritoryBarChart />
        </section>

        {/* Quick Navigation */}
        <section aria-label="Module Navigation" className="section-gap">
          <QuickNavPanel />
        </section>

        {/* Bottom Row: Top Accounts + Activity Feed */}
        <section aria-label="Accounts and Activity" className="section-gap">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-3 md:gap-4 lg:gap-5">
            <div className="xl:col-span-2">
              <TopAccountsTable />
            </div>
            <div>
              <RecentActivityFeed />
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="text-center py-4 border-t border-border mt-2">
          <p className="font-caption text-xs text-muted-foreground">
            iSYNC Distribution Ordering &amp; Sales Intelligence System &copy; {new Date()?.getFullYear()} &nbsp;·&nbsp; All rights reserved
          </p>
        </footer>
      </main>
    </div>
  );
}